import React, { JSX, FC, useEffect, useState, useMemo, useContext, useCallback } from "react";
import { NavigateFunction, useNavigate, useParams } from "react-router";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "redux/store/store";
import { createProduct, updateProduct } from "redux/thunks/products.thunk";
import * as CategoriesAPI from "api/categories";

import { Checkbox, Form, Input, notification, Select, Tooltip } from "antd";
import PhotoBlock from "components/Products/PhotoBlock";
import Categories from "./Categories/Categories";
import Properties from "./Properties/Properties";

import "./style.scss";

import { FormInstance, useWatch } from "antd/es/form/Form";
import { CategoryTPType, CategoryTreeRequestType, CategoryTreeType, ChildrenCategoryType } from "categories/types";
import { DraggerFileType } from "components/ui/DragAndDrop/DragAndDrop";
import { AppStateType } from "redux/reducers/mainReducer";
import {
  IFormFields,
  ProductContext,
  ProductFieldsFormType,
  ProductMasterFieldNameType,
  ProductMasterFormType,
  ProductMasterPropertyType,
  ProductStatus
} from "pages/ImportMasterPage";
import {
  CurrencyCharCodeType,
  ModificationType,
  ProductCreateRequestType,
  ProductType,
  ProductUpdateRequestType
} from "products/types";
import { CheckboxChangeEventTarget } from "antd/es/checkbox/Checkbox";
import { APP_PATHES } from "constants/appPathes";
import {
  buildCategoryTreeFromChain,
  getCategoryParents,
  OptionType,
  transformDataToCategory,
} from "utils/formatCategories";
import PriceInput, { PriceValueType } from "./PriceInput";
import { DefaultOptionType } from "antd/es/select";
import { handleError } from "utils/handleError";
import { showMessage } from "utils/showMessage";

export type UpdateTargetType = EventTarget & (HTMLInputElement | HTMLElement)
  | CheckboxChangeEventTarget
  | React.MouseEvent<HTMLElement, MouseEvent>
  | EventTarget
  | string;
type FieldHandlerType = (value?: string | number | PriceValueType) => void;
type FieldMappingsType = Record<ProductMasterFieldNameType, FieldHandlerType | null>;
type updateCharcPropsType = { target: UpdateTargetType }

interface IImportProductForm {
}

const ImportProductForm: FC<IImportProductForm> = (): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate: NavigateFunction = useNavigate();
  const { dataId, modId } = useParams<{ mode: string, dataId?: string, modId?: string }>();

  const form = useFormInstance<IFormFields>();
  const values = useWatch<FormInstance<IFormFields>>([], form);

  const { setIsFocused } = useContext(ProductContext);

  const { settings: { ndsRate } } = useSelector((state: AppStateType) => state.suppliersSlice);
  const { selected: selectedProduct } = useSelector((state: AppStateType) => state.productsSlice?.products);
  const { selected: selectedModification } = useSelector((state: AppStateType) => state.productsSlice?.modifications);

  const ndsOptions: DefaultOptionType[] = [
    { label: "Без НДС", value: "no_nds" },
    { label: "НДС 0%", value: "0" },
    { label: "НДС 5%", value: "5" },
    { label: "НДС 7%", value: "7" },
    { label: "НДС 10%", value: "10" },
    { label: "НДС 22%", value: "22" },
  ];

  const [isResetFileList, setIsResetFileList] = useState<boolean>(false);
  const [selectedCategoryOption, setSelectedCategoryOption] = useState<OptionType[]>([]);
  const [isShowNotification, setIsShowNotification] = useState<boolean>(false);
  const [productStatus, setProductStatus] = useState<string>("");

  const isRequiredFields: boolean = useMemo(() => {
    return !!values?.price?.price
      && !!values?.price?.currency
      && !!values?.name
      && !!values?.productName
      && !!values?.productCode;
  }, [
    values?.price?.price,
    values?.price?.currency,
    values?.name,
    values?.productName,
    values?.productCode
  ]);
  const hasWarehouse: boolean = useMemo(() => !!values?.warehouse, [values?.warehouse]);
  const isEditMode: boolean = useMemo(() => !!dataId, [dataId]);
  const isModificationSelected: boolean = useMemo(() => !!modId, [modId]);
  const isFieldsDisabled: boolean = useMemo(() => (
    !hasWarehouse || !!dataId && !isModificationSelected
  ), [hasWarehouse, dataId, isModificationSelected]);

  const productTooltip: string = useMemo(() => {
    const messages: { condition: boolean; message: string; }[] = [
      { condition: !!dataId && !isModificationSelected, message: "Выберите модификацию из списка" },
    ];

    return messages.find(({ condition }) => condition)?.message || "";
  }, [hasWarehouse, isModificationSelected]);

  const baseModification: ModificationType = useMemo(() => (
    selectedProduct?.modifications?.find(({ is_base }) => !!is_base)
  ), [selectedProduct?.modifications]);

  const isBase = useMemo(() => (
    baseModification?.id?.toString() === modId
  ), [modId, baseModification?.id]);

  const isShowInCatalogDisabled: boolean = useMemo(() => (
    isFieldsDisabled
    || !isEditMode
    || (!isBase && !baseModification?.show_in_catalog)
    || !isRequiredFields
  ), [
    baseModification?.show_in_catalog,
    isBase,
    isEditMode,
    isFieldsDisabled,
    isRequiredFields
  ]);

  useEffect(() => {
    setProductStatus(selectedProduct?.status);
  }, [selectedProduct?.status]);

  useEffect(() => {
    return () => { // Очистка картинок
      setIsResetFileList(false);
      setIsResetFileList(true);
    };
  }, []);

  useEffect(() => {
    if (!isRequiredFields && values?.showInCatalog) {
      form.setFieldsValue({ showInCatalog: false });

      updateCharc({ target: { type: "checkbox", checked: false } });
    }
  }, [isRequiredFields, values?.showInCatalog]);

  // Заполнение формы товара
  useEffect(() => {
    selectedProduct && !!Number(modId) && fillForm(selectedProduct, Number(modId));
  }, [selectedProduct]);

  // При открытии/смене модификации
  useEffect(() => {
    setIsResetFileList(false);
    if (selectedModification) {
      setIsResetFileList(true);

      const newFormValues: ProductFieldsFormType = {
        ...fillFormModification(selectedModification)
      };

      form.setFieldsValue(newFormValues);
    }
  }, [selectedModification]);

  const handleFocus = useCallback((): void => setIsFocused?.(true), [setIsFocused]);

  const checkPrice = (_: any, value: { price: number, currency: string }): Promise<void> => {
    if (!value?.currency) {
      return Promise.reject(new Error("Заполните все значения"));
    }
    return Promise.resolve();
  };

  const transformProperty = (obj: Record<string, string>): ProductMasterPropertyType[] => {
    return Object.entries(obj).map(([key, value]) => ({
      propertyName: key,
      propertyValue: value,
    }));
  };

  // Fill form
  const fillForm = (product: ProductType, modificationId: number): void => {
    if (!product) return;

    const { category_bk_id, code, name, modifications, rate_nds } = product;
    const modification: ModificationType = modifications?.find(({ id }: { id: number }) => id === modificationId);

    // Получаем дочернюю категорию
    category_bk_id && addCategoryChainToOptions(category_bk_id);

    const currentValues: IFormFields = form.getFieldsValue();
    const newFormValues: ProductFieldsFormType = {
      productName: name,
      productCode: code,
      categoryBK: [category_bk_id],
      ...(rate_nds === null || isNaN(rate_nds) ? { nds: "no_nds" } : { nds: `${rate_nds}` }),
      ...fillFormModification(modification)
    };

    // Перезаписываем только те поля, которые имеют новые значения
    const fieldsToSet = Object.keys(newFormValues).reduce((acc, key) => {
      if (key === "categoryBK") {
        const getLastValue = (value: number | number[]): number => Array.isArray(value) ? value.at(-1) : value;

        const newFormValuesKey: number = getLastValue(newFormValues[key]);
        const currentValuesKey: number = getLastValue(currentValues[key]);

        if (newFormValuesKey !== currentValuesKey) {
          acc[key] = newFormValues[key];
        }
      } else {
        if (newFormValues[key] !== currentValues[key]) {
          acc[key] = newFormValues[key];
        }
      }

      return acc;
    }, {});

    form.setFieldsValue(fieldsToSet);
    form.validateFields(["categoryBK"]);
  };

  const fillFormModification = (modification?: ModificationType): ProductMasterFormType => {
    if (!modification) return null;

    const canAddProperties: boolean = !!modification?.property;
    const propertiesArray: any[] = canAddProperties ? transformProperty(modification.property) : [];
    const { code, currency, currency_price, is_base, min_count, name, show_in_catalog } = modification;

    return {
      name,
      code,
      minCount: min_count?.toString() || "",
      showInCatalog: show_in_catalog,
      description: modification?.description || "",
      properties: canAddProperties ? propertiesArray : [],
      price: {
        ...(currency_price && { price: currency_price }),
        ...(currency && { currency }),
      },
      isBase: !!is_base,
    } as ProductMasterFormType;
  };

  // Categories
  const fetchCategory = async (params: CategoryTreeRequestType): Promise<CategoryTreeType> => {
    const findedCategory: CategoryTreeType = (
      await CategoriesAPI.getCategoriesTPAllTree(params)
    ).data.at(0);

    return findedCategory;
  };

  const getProductCategoryChain = (
    parents: CategoryTPType[],
    currentChild: ChildrenCategoryType,
  ): OptionType[] => {
    const newParents: ChildrenCategoryType[] = getCategoryParents(parents);

    const parentsWithChildren: ChildrenCategoryType[] = newParents.map((item: ChildrenCategoryType, index: number) => ({
      ...item,
      children_ids: index < newParents.length - 1 ? [newParents[index + 1]] : [currentChild]
    }));

    return transformDataToCategory([...parentsWithChildren, currentChild]);
  };

  const buildCategoryOptions = (
    categoryData: CategoryTreeType,
    includeChildrenIds: boolean = false
  ): OptionType[] => {
    const hasChildren: boolean = !!categoryData?.children_ids?.length;
    const parents: CategoryTPType[] = categoryData?.parent as CategoryTPType[];

    const currentChild: ChildrenCategoryType = {
      id: categoryData?.id,
      name: categoryData?.name,
      has_children: hasChildren,
      parent: parents?.[parents?.length - 1]?.id,
      ...(includeChildrenIds && hasChildren && { children_ids: categoryData?.children_ids }),
    };

    const chain: OptionType[] = getProductCategoryChain(parents, currentChild);

    if (!chain.length) return null;

    return buildCategoryTreeFromChain(chain);
  };

  const addCategoryChainToOptions = async (childId: number): Promise<OptionType[]> => {
    const categoryData: CategoryTreeType = await fetchCategory({ category_ids: [childId] });

    const newOption: OptionType[] = buildCategoryOptions(categoryData, true);

    if (!newOption) return null;

    form.setFieldValue("categoryBK", childId);
    setSelectedCategoryOption(newOption);

    return newOption;
  };

  // Images
  const addMediaHandler = (file: DraggerFileType): DraggerFileType => {
    const updateRequestBody: ProductUpdateRequestType = {
      modification: {
        id: Number(modId),
        preload_files: [file.responseId],
      }
    };

    updateProductHandler(updateRequestBody);

    return file;
  };

  const deleteMediaHandler = (fileList: DraggerFileType[]): DraggerFileType[] => {
    updateProductHandler({
      modification: {
        id: Number(modId),
        loaded_files: fileList?.map(({ uid }) => uid)
      }
    });

    return fileList;
  };

  // Product
  const createProductHandler = (): void => {
    const { productName, productCode, warehouse } = values;
    const canCreate: boolean = !!productName && !!productCode;

    if (canCreate) {
      const createRequestBody: ProductCreateRequestType = {
        name: productName,
        code: productCode,
        status: ProductStatus.DRAFT,
        warehouse_id: warehouse,
        rate_nds: ndsRate?.toString(),
      };

      dispatch(createProduct(createRequestBody))
        .then((response: ProductType) => {
          notification.info({
            message: "Черновик создан"
          });
          navigate(`${APP_PATHES.IMPORT_MASTER_MANUAL}/${response.id}`);
        })
        .catch((error) => {
          let message: string = "Ошибка при создании товара";
          if (error?.response?.data?.message?.includes("артикул")) {
            message = "Черновик не сохранен";

            form.setFields([{
              name: "productCode",
              value: "",
              errors: [error?.response?.data?.message]
            }]);
          }

          handleError(message, error);
        });
    }
  };

  const updateProductHandler = (body: ProductUpdateRequestType): void => {
    dispatch(updateProduct(Number(dataId), body))
      .then(({ status }) => {
        if (status === "Черновик" && productStatus !== "Черновик" && !isShowNotification) {
          showMessage("success", "Черновик сохранен");
          setIsShowNotification(true);
        }

        if (status !== "Черновик") {
          setIsShowNotification(false);
        }
      })
      .catch((error) => {
        let message: string = "Ошибка при обновлении товара";
        if (error?.response?.data?.message?.includes("артикул")) {
          message = "Черновик не сохранен";

          form.setFields([{
            name: "productCode",
            value: "",
            errors: [error?.response?.data?.message]
          }]);
        }
        handleError("Ошибка при обновлении товара", error);
      })
      .finally(() => setIsFocused(false));
  };

  const updateCharc = ({ target }: updateCharcPropsType): void => {
    let fieldName: ProductMasterFieldNameType;
    let fieldValue: string;
    const currentFormValues: IFormFields = form.getFieldsValue();

    if (typeof target === "string" && target !== "properties") {
      fieldName = target as ProductMasterFieldNameType;
      fieldValue = currentFormValues?.[target];
    } else {
      const { id, value, type, checked } = target as HTMLInputElement | CheckboxChangeEventTarget ?? {};

      if (id?.includes("properties") || target === "properties") {
        fieldName = "properties";
        fieldValue = type === "checkbox" ? `${checked}` : value || {};
      } else {
        fieldName = id as ProductMasterFieldNameType;
        fieldValue = type === "checkbox" ? `${checked}` : value;
      }
    }

    const hasFieldValue: boolean = !!fieldValue || fieldValue === "";
    const hasValue: boolean = !!currentFormValues?.[fieldName] || currentFormValues?.[fieldName] === "";

    if (!hasFieldValue && !hasValue) return;

    const requestBody: ProductUpdateRequestType = {
      modification: {
        id: Number(modId),
        show_in_catalog: false,
        warehouse_id: Number(currentFormValues?.warehouse)
      }
    };

    const updateCurrencyFields = (price: number, currency: CurrencyCharCodeType): void => {
      if (!!currency && (!isNaN(price) || price === undefined)) {
        Object.assign(requestBody.modification, {
          currency_price: price || 0,
          ...(!!price && { currency }),
        });

        if (!requestBody.modification.currency_price) {
          requestBody.status = ProductStatus.DRAFT;
        }

        // если товар изначально был в статусе "Размещен" и изменили цену,
        // но не удаляли и не обнуляли ее, то не переводим товар в черновик
        if (selectedProduct.status === ProductStatus.PUBLISHED && fieldName === "price" && !!price && !!currency) {
          requestBody.status = ProductStatus.PUBLISHED;
          requestBody.modification.show_in_catalog = selectedModification.show_in_catalog;
        }
      }
    };

    const categoryBK: number = Array.isArray(currentFormValues?.categoryBK)
      ? currentFormValues?.categoryBK?.at(-1)
      : currentFormValues?.categoryBK;

    const updateProperties = (): void => {
      const properties: ProductMasterPropertyType[] = form.getFieldValue("properties");
      const canUpdate: boolean = !properties
        ?.some(({ propertyName, propertyValue }) => !propertyName || !propertyValue);

      if (canUpdate) {
        requestBody.modification.property = properties
          ?.filter((property: ProductMasterPropertyType) => !!property?.propertyName && !!property?.propertyValue)
          ?.reduce((acc, { propertyName, propertyValue }) => {
            acc[propertyName] = propertyValue;
            return acc;
          }, {} as Record<string, string>);
      }
    };

    const fieldMappings: FieldMappingsType = {
      productName: (value: string) => (value ? requestBody.name = value : null),
      productCode: (value: string) => (value ? requestBody.code = value : null),
      categoryBK: () => (requestBody.category = categoryBK === null ? 0 : categoryBK),
      properties: () => updateProperties(),
      name: (value: string) => (value ? requestBody.modification.name = value : null),
      code: (value: string) => (requestBody.modification.code = value),
      minCount: (value: string) => (requestBody.modification.min_count = Number(value)),
      description: (value: string) => (requestBody.modification.description = value),
      price: (value: PriceValueType) => updateCurrencyFields(value?.price, value?.currency),
      nds: (value: string) => (requestBody.rate_nds = value),
      showInCatalog: () => (requestBody.modification.show_in_catalog = (target as CheckboxChangeEventTarget).checked),
      isBase: () => (requestBody.modification.is_base = (target as CheckboxChangeEventTarget).checked),
      photo: null,
    };

    fieldMappings?.[fieldName]?.(fieldValue);

    const isProductHasChanges: boolean = Object.entries(requestBody)
      .some(([key, value]) => key !== "modification"
        && value !== selectedProduct?.[key]
      );
    const isModificationHasChanges: boolean = Object.entries(requestBody.modification)
      .some(([key, value]) => key !== "id"
        && (key !== "warehouse_id" || value !== selectedModification?.warehouse_id)
        && value !== selectedModification?.[key]
      );
    const hasChanges: boolean = isProductHasChanges || isModificationHasChanges;

    if (!hasChanges) {
      setIsFocused(false);
      return;
    }

    (!!dataId || !!dataId && !!modId) ? updateProductHandler(requestBody) : createProductHandler();
  };

  return (
    <div className="ManualPropductForm_wrapper">
      <div className="importProductForm_wrapper">
        <Tooltip title={productTooltip}>
          <Form.Item
            key="productNameField"
            name="productName"
            label="Наименование продукта"
            rules={[
              {
                required: true,
                message: "Это поле обязательно для заполнения",
              },
            ]}
            className="mb-4"
          >
            <Input
              onFocus={handleFocus}
              onBlur={updateCharc}
              size="large"
              placeholder="Введите наименование продукта"
              disabled={isFieldsDisabled}
            />
          </Form.Item>
        </Tooltip>
        <Tooltip title={productTooltip}>
          <Form.Item
            key="productCodeField"
            name="productCode"
            label="Артикул продукта"
            rules={[
              {
                required: true,
                message: "Это поле обязательно для заполнения",
              },
            ]}
            className="mb-4"
          >
            <Input
              onFocus={handleFocus}
              onBlur={updateCharc}
              size="large"
              placeholder="Введите артикул продукта"
              disabled={isFieldsDisabled}
            />
          </Form.Item>
        </Tooltip>
      </div>
      <div className="importModificationForm_wrapper">
        <Tooltip title={productTooltip}>
          <Categories
            form={form}
            handleFocus={handleFocus}
            updateCharc={updateCharc}
            selectedCategoryOption={selectedCategoryOption}
            setSelectedCategoryOption={setSelectedCategoryOption}
            fetchCategory={fetchCategory}
            isFieldsDisabled={isFieldsDisabled || !isEditMode}
            buildCategoryOptions={buildCategoryOptions}
          />
        </Tooltip>
        <Form.Item
          key="nameField"
          name="name"
          label="Наименование модификации"
          rules={[
            {
              required: true,
              message: "Это поле обязательно для заполнения",
            },
          ]}
          className="mb-4"
        >
          <Input
            onFocus={handleFocus}
            onBlur={updateCharc}
            size="large"
            placeholder="Введите наименование модификации"
            disabled={isFieldsDisabled || !isEditMode}
          />
        </Form.Item>
        <Form.Item
          key="codeField"
          name="code"
          label="Артикул модификации"
          className="mb-4"
        >
          <Input
            onFocus={handleFocus}
            onBlur={updateCharc}
            size="large"
            placeholder="Введите артикул модификации"
            disabled={isFieldsDisabled || !isEditMode}
          />
        </Form.Item>
        <Form.Item
          key="minCount"
          name="minCount"
          label="Минимальное кол-во для заказа"
          className="mb-4"
        >
          <Input
            onFocus={handleFocus}
            onBlur={updateCharc}
            size="large"
            placeholder="Введите число"
            disabled={isFieldsDisabled || !isEditMode}
          />
        </Form.Item>
        <Form.Item
          key="descriptionField"
          name="description"
          label="Описание модификации"
          className="mb-4"
        >
          <Input
            onFocus={handleFocus}
            onBlur={updateCharc}
            size="large"
            placeholder="Введите описание модификации"
            disabled={isFieldsDisabled || !isEditMode}
          />
        </Form.Item>
        <Form.Item
          required
          name="price"
          label="Цена"
          className="mb-4"
          rules={[{ validator: checkPrice }]}
        >
          <PriceInput
            onFocus={handleFocus}
            onChange={(_value) => updateCharc({ target: "price" })}
            isDisabled={isFieldsDisabled || !isEditMode}
          />
        </Form.Item>
        <Form.Item
          name="nds"
          label="НДС"
          className="mb-4 w-fit"
        >
          <Select
            size="large"
            placeholder="Выберите НДС"
            style={{ minWidth: "140px" }}
            onChange={() => updateCharc({ target: "nds" })}
            options={ndsOptions}
            disabled={isFieldsDisabled || !isEditMode}
          />
        </Form.Item>
        <Properties
          form={form}
          handleFocus={handleFocus}
          updateCharc={updateCharc}
          isEditMode={isEditMode}
          isFieldsDisabled={isFieldsDisabled}
        />
        <PhotoBlock
          modification={selectedModification}
          addMedia={addMediaHandler}
          deleteMedia={deleteMediaHandler}
          isDisabledFields={isFieldsDisabled || !isEditMode}
          resetFileList={isResetFileList}
        />
        <Form.Item
          key="showInCatalogField"
          name="showInCatalog"
          valuePropName="checked"
          className="ml-0 mb-0"
        >
          <Checkbox
            onChange={updateCharc}
            disabled={isShowInCatalogDisabled}
          >
            Показать в каталоге
          </Checkbox>
        </Form.Item>
        <Form.Item
          key="isBaseField"
          name="isBase"
          valuePropName="checked"
          className="ml-0 mb-0"
        >
          <Checkbox
            onChange={updateCharc}
            disabled={isFieldsDisabled || !isEditMode || selectedModification?.is_base}
          >
            Базовая модификация
          </Checkbox>
        </Form.Item>
      </div>
    </div>
  );
};

export default ImportProductForm;
